import {
  type GetFolderAssetsArgs,
  type SearchResult,
  useInfiniteScrollFromCache,
  useLazyGetFolderAssetsQuery,
} from '@carboncopyco/data';
import { FolderGrayIllustration } from '@carboncopyco/illustrations';
import type { Entity } from '@carboncopyco/types';
import { Container, EmptyView, SkeletonImageLoading } from '@carboncopyco/ui';
import { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { InfiniteList } from '../../../../InfiniteList';
import { useUrlState } from '../../../../utils';
import { AssetGridSection } from '../../AssetGridSection';
import { useWorkspacePageProps } from '../../WorkspacePageContext';
import { WorkspacePageControls } from '../../WorkspacePageControls';
import { WorkspacePageEmptySearch } from '../../WorkspacePageEmptySearch';
import { ALL_ENTITIES, INITIAL_URL_STATE } from '../../consts';
import type { UrlState } from '../../types';
import { FolderPageTableView } from '../FolderPageTableView';

type DirtyRef = {
  isDirty: boolean;
  resetControls: () => void;
};

export const FolderPageBodyArchived = () => {
  const { workspaceId } = useWorkspacePageProps();
  const [urlState] = useUrlState<UrlState>(INITIAL_URL_STATE);
  const { viewMode, entities, creators, query, sort } = urlState;
  const dirtyRef = useRef<DirtyRef>({
    isDirty: false,
    resetControls: () => undefined,
  });
  const isDirty = dirtyRef?.current?.isDirty;
  const { folderId } = useParams();
  const { organizationId } = useWorkspacePageProps();

  const fetchArgs = useMemo<GetFolderAssetsArgs>(
    () => ({
      organizationId,
      workspaceId,
      creators: creators ? creators?.split(',') : undefined,
      sort,
      deletedEntities: true,
      entities: entities
        ? (entities.split(',') as Entity[])
        : (ALL_ENTITIES as Entity[]),
      query: query && query,
      limit: 30,
      folderId,
    }),
    [organizationId, workspaceId, creators, sort, entities, query, folderId]
  );

  const {
    items,
    isLoading,
    isLoadingBatch,
    error,
    fetchFirstBatch,
    fetchNextBatch,
  } = useInfiniteScrollFromCache<SearchResult, GetFolderAssetsArgs>(
    useLazyGetFolderAssetsQuery,
    fetchArgs
  );

  const isEmpty = !items?.length && !isLoading && !isLoadingBatch;

  useEffect(() => {
    fetchFirstBatch();
  }, [fetchArgs]);

  if (isEmpty && !isDirty) {
    return (
      <EmptyView
        illustration={<FolderGrayIllustration />}
        title="Empty folder"
      />
    );
  }

  return (
    <>
      <WorkspacePageControls
        ref={dirtyRef}
        archive
      />
      {isEmpty && isDirty ? (
        <WorkspacePageEmptySearch
          resetControls={dirtyRef.current.resetControls}
        />
      ) : (
        <div className="w-full flex justify-center overflow-y-hidden flex-auto py-5">
          <div className="w-ds-page h-full flex overflow-y-hidden relative">
            {viewMode === 'tiles' ? (
              <InfiniteList
                error={
                  error && {
                    message: 'Error fetching assets',
                  }
                }
                isLoading={isLoading}
                isLoadingBatch={isLoadingBatch}
                loader={
                  <Container>
                    <SkeletonImageLoading
                      cols={4}
                      rows={4}
                    />
                  </Container>
                }
                onReachBottom={fetchNextBatch}
              >
                <AssetGridSection
                  items={items}
                  archived
                  inFolder
                />
              </InfiniteList>
            ) : (
              <FolderPageTableView
                isLoadingBatch={isLoadingBatch}
                items={items as SearchResult[]}
                isLoading={isLoading}
                error={error}
                fetchNextBatch={fetchNextBatch}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};
